/**
 * Artifacts CRUD (new `/artifacts` endpoints on Nest).
 *
 * Insert is AI-owned (Celery job `artifacts.persist_from_stream`). The FE
 * only reads, updates, and deletes here. The one surviving legacy
 * endpoint is `getArtifactBySlug` (public `/a/:slug` page).
 */

import type { AxiosError } from 'axios';
import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type {
  Artifact,
  ArtifactDemoLink,
  UpdateArtifactPayload,
} from '../../types/artifact.types';

/** Parse a `structured_content` value that may arrive as an object or a JSON string. */
const parseStructured = (raw: unknown): Record<string, unknown> | null => {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * The by-scope / by-project endpoints return `404` when zero artifacts
 * exist. The UI wants an empty list; swallow 404 and rethrow anything else.
 */
const emptyOn404 = <T>(err: unknown, fallback: T): T => {
  const status = (err as AxiosError | undefined)?.response?.status;
  if (status === 404) return fallback;
  throw err;
};

/**
 * The Nest entity exposes fields as camelCase (`demoUrl`,
 * `artifactType`, `llmInteractionId`), but every consumer in this
 * codebase reads snake_case. Normalise here so the outward-facing
 * `Artifact` type stays snake_case and the ~20 callsites in web/ui
 * don't need to change.
 */
const normalizeArtifact = (raw: any): Artifact => {
  if (!raw || typeof raw !== 'object') return raw as Artifact;
  return {
    id: raw.id,
    llm_interaction_id: raw.llm_interaction_id ?? raw.llmInteractionId,
    artifact_type: raw.artifact_type ?? raw.artifactType,
    content: raw.content ?? null,
    structured_content: parseStructured(raw.structured_content ?? raw.structuredContent),
    demo_url: raw.demo_url ?? raw.demoUrl ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
};

const unwrapList = (body: any): Artifact[] => {
  const rows = Array.isArray(body)
    ? body
    : Array.isArray(body?.data)
      ? body.data
      : [];
  return rows.map(normalizeArtifact);
};

const unwrapOne = <T>(body: any): T => {
  const raw =
    body && typeof body === 'object' && 'data' in body && !('id' in body)
      ? (body as { data: unknown }).data
      : body;
  return raw as T;
};

const unwrapArtifact = (body: any): Artifact =>
  normalizeArtifact(unwrapOne<any>(body));

class ArtifactService {
  /** GET /artifacts/by-scope/:scopeId */
  async listScopeArtifacts(scopeId: string): Promise<Artifact[]> {
    try {
      const response = await apiClient.get(ApiUrls.listScopeArtifacts(scopeId));
      return unwrapList(response.data);
    } catch (err) {
      return emptyOn404(err, [] as Artifact[]);
    }
  }

  /** GET /artifacts/by-project/:projectId */
  async listStartupArtifacts(projectId: string): Promise<Artifact[]> {
    try {
      const response = await apiClient.get(ApiUrls.listStartupArtifacts(projectId));
      return unwrapList(response.data);
    } catch (err) {
      return emptyOn404(err, [] as Artifact[]);
    }
  }

  /** GET /artifacts?llm_interaction_id=... */
  async listArtifactsByLlmInteraction(llmInteractionId: string): Promise<Artifact[]> {
    const response = await apiClient.get(
      ApiUrls.listArtifactsByLlmInteraction(llmInteractionId),
    );
    return unwrapList(response.data);
  }

  /** GET /artifacts/:id */
  async getArtifactById(id: string): Promise<Artifact> {
    const response = await apiClient.get(ApiUrls.getArtifactById(id));
    return unwrapArtifact(response.data);
  }

  /** PUT /artifacts/:id — partial full-update (whitelisted fields only). */
  async updateArtifact(id: string, payload: UpdateArtifactPayload): Promise<Artifact> {
    const response = await apiClient.put(ApiUrls.updateArtifact(id), payload);
    return unwrapArtifact(response.data);
  }

  /**
   * Publish the rendered HTML to S3 and receive the updated row (with
   * `demo_url` populated). FE is responsible for rendering the current
   * markdown → HTML before calling this — the server just stores the
   * bytes and updates the column.
   */
  async publishArtifactToS3(id: string, html: string): Promise<Artifact> {
    const response = await apiClient.post(ApiUrls.publishArtifact(id), { html });
    return unwrapArtifact(response.data);
  }

  /** DELETE /artifacts/:id */
  async deleteArtifact(id: string): Promise<void> {
    await apiClient.delete(ApiUrls.deleteArtifact(id));
  }

  /**
   * GET a published artifact by slug — used by the public `/a/:slug`
   * page (PublishedArtifact). This is the only surviving
   * `artifact-demo-links` endpoint.
   */
  async getArtifactBySlug(slug: string): Promise<ArtifactDemoLink> {
    const response = await apiClient.get(ApiUrls.getArtifactBySlug(slug));
    return unwrapOne<ArtifactDemoLink>(response.data);
  }
}

export const artifactService = new ArtifactService();
export default artifactService;
